import { useEffect, useRef, useState } from "react";
import { useCartCount } from "../../hooks";
import { cartUrl, onProductAddedToCart, type WooProductSummary } from "../../wordpress";

/** Preço já formatado pelo WooCommerce: moeda, promoção, faixa de variável e sufixo de IVA. */
export function ProductPrice({ product }: { product: WooProductSummary }) {
	if (!product.priceHtml) return null;

	return (
		// eslint-disable-next-line react/no-danger -- HTML de confiança, gerado pelo próprio WooCommerce.
		<span className="product-price" dangerouslySetInnerHTML={{ __html: product.priceHtml }} />
	);
}

/** Duração do "✓ Afegit" antes do botão voltar ao texto normal. */
const CONFIRMATION_MS = 2200;

/**
 * Reaproveita o mecanismo de AJAX que o WooCommerce já carrega em toda página
 * (`wc-add-to-cart.js`, ligado a `document.body`): basta que o link tenha as
 * classes e o `data-product_id` que esse script já sabe reconhecer. Produtos
 * variáveis, agrupados ou esgotados não suportam adição direta — nesses casos
 * `isAjaxAddToCart` é falso e o link vira uma navegação comum até a página do
 * produto, exatamente como o WooCommerce já faz nos seus próprios templates.
 *
 * Ao confirmar a adição, mostra "✓ Afegit" só neste botão — identificado pelo
 * elemento que o próprio WooCommerce devolve no evento, não por um estado
 * global que acenderia todos os botões da grade ao mesmo tempo.
 */
export function AddToCartButton({ product }: { product: WooProductSummary }) {
	const ref = useRef<HTMLAnchorElement | null>(null);
	const [justAdded, setJustAdded] = useState(false);

	useEffect(() => {
		if (!product.isAjaxAddToCart) return;

		return onProductAddedToCart((button) => {
			if (button !== ref.current) return;

			setJustAdded(true);
			const timeout = setTimeout(() => setJustAdded(false), CONFIRMATION_MS);
			return () => clearTimeout(timeout);
		});
	}, [product.isAjaxAddToCart]);

	if (!product.addToCartUrl) return null;

	const className = [
		"btn",
		"btn--yellow",
		"add_to_cart_button",
		product.isAjaxAddToCart ? "ajax_add_to_cart" : "",
		justAdded ? "is-added" : "",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<a
			ref={ref}
			href={product.addToCartUrl}
			className={className}
			data-product_id={product.id}
			data-quantity="1"
			rel="nofollow"
		>
			{justAdded ? "✓ Afegit" : product.addToCartText || "Afegeix a la cistella"}
		</a>
	);
}

/**
 * Fica visível sempre que a cistella já tem algo — não só logo após um clique
 * nesta página. O elemento em si nunca sai do DOM: só o atributo `hidden`
 * muda. Ficasse condicional (`return null` de carrinho vazio), o primeiro
 * "Afegeix" da sessão duplicaria o link — o script do WooCommerce checa se já
 * existe um `.added_to_cart` como irmão do botão antes de inserir o dele, e
 * naquele instante (carrinho ainda em 0 quando o clique acontece) não
 * encontraria o nosso a tempo.
 */
function ViewCartLink() {
	const count = useCartCount();

	return (
		<a href={cartUrl()} className="added_to_cart wc-forward" hidden={count <= 0}>
			Visualitza la cistella
		</a>
	);
}

/**
 * "Afegeix a la cistella" fixo no canto inferior esquerdo do cartão e
 * "Visualitza la cistella" no oposto, quando visível — ver `.product-actions`
 * em ContentPage.css para o empurrão ao rodapé do cartão.
 */
export function ProductActions({ product }: { product: WooProductSummary }) {
	return (
		<div className="product-actions">
			<AddToCartButton product={product} />
			<ViewCartLink />
		</div>
	);
}
